import { useState, useCallback, useMemo, useRef } from 'react';
import { ZoomIn, ZoomOut, Printer, RotateCcw } from 'lucide-react';
import type { ClientFloorPlan, ClientFloorPlanObject } from '../../api/portal';
import { CanvasItemShape } from './CanvasItemShape';
import type { CanvasItem } from './types';
import { ftToPx, PIXELS_PER_FOOT } from './helpers';
import { isTableItem } from './types';

const NOOP = () => {};

function toCanvasItem(o: ClientFloorPlanObject): CanvasItem {
  return {
    id: o.id,
    objectType: o.objectType,
    label: o.label ?? '',
    x: o.x,
    y: o.y,
    widthFt: o.widthFt ?? 6,
    heightFt: o.heightFt ?? 6,
    rotation: o.rotation,
    color: o.color,
    opacity: o.opacity,
    seats: o.seats ?? 0,
    config: o.config ?? {},
    sortOrder: o.sortOrder,
    ...(o.tableNumber != null ? { tableNumber: o.tableNumber } : {}),
  };
}

function computeBounds(objects: ClientFloorPlanObject[]): {
  width: number;
  height: number;
} {
  if (objects.length === 0) return { width: 1200, height: 800 };
  let maxX = 0;
  let maxY = 0;
  for (const o of objects) {
    const w = ftToPx(o.widthFt ?? 6);
    const h = ftToPx(o.heightFt ?? 6);
    if (o.x + w > maxX) maxX = o.x + w;
    if (o.y + h > maxY) maxY = o.y + h;
  }
  return {
    width: Math.max(maxX + PIXELS_PER_FOOT * 4, 600),
    height: Math.max(maxY + PIXELS_PER_FOOT * 4, 400),
  };
}

interface ClientFloorPlanCanvasProps {
  plan: ClientFloorPlan;
  eventName?: string;
  /** Per-client "allowed print" permission — hides the print action when false. */
  canPrint?: boolean;
}

export function ClientFloorPlanCanvas({
  plan,
  eventName,
  canPrint = false,
}: ClientFloorPlanCanvasProps) {
  const [zoom, setZoom] = useState(1);
  const svgRef = useRef<SVGSVGElement>(null);

  const items = useMemo(() => plan.objects.map(toCanvasItem), [plan.objects]);

  const { width: canvasW, height: canvasH } = useMemo(
    () => computeBounds(plan.objects),
    [plan.objects],
  );

  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.15, 3)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.15, 0.2)), []);
  const resetZoom = useCallback(() => setZoom(1), []);

  const handlePrint = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const svgClone = svgEl.cloneNode(true) as SVGElement;
    svgClone.setAttribute('width', String(canvasW));
    svgClone.setAttribute('height', String(canvasH));

    const planLabel = plan.roomName ? `${plan.name} — ${plan.roomName}` : plan.name;
    const title = eventName ? `${eventName}: ${planLabel}` : planLabel;
    const tableCount = plan.objects.filter((o) => o.tableNumber != null).length;
    const totalSeats = plan.objects.reduce((s, o) => s + (o.seats ?? 0), 0);

    const printWin = window.open('', '_blank', 'width=1200,height=900');
    if (!printWin) return;

    printWin.document.write(
      `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title>` +
        `<style>` +
        `*{print-color-adjust:exact;-webkit-print-color-adjust:exact;}` +
        `@page{size:landscape;margin:10mm;}` +
        `body{margin:0;padding:10mm;font-family:sans-serif;box-sizing:border-box;}` +
        `.ph{display:flex;align-items:flex-start;gap:12px;margin-bottom:12px;padding-bottom:10px;border-bottom:2px solid #333;}` +
        `.ph-details h1{margin:0 0 4px 0;font-size:18px;color:#111;}` +
        `.ph-details p{margin:2px 0;font-size:11px;color:#555;}` +
        `</style></head><body>` +
        `<div class="ph"><div class="ph-details">` +
        `<h1>${title}</h1>` +
        (tableCount > 0
          ? `<p>Tables: ${tableCount} &nbsp;·&nbsp; Total Seats: ${totalSeats}</p>`
          : '') +
        `</div></div>` +
        svgClone.outerHTML +
        `<script>window.onload=function(){setTimeout(function(){window.print();},400);}</script>` +
        `</body></html>`,
    );
    printWin.document.close();
  }, [plan, canvasW, canvasH, eventName]);

  // Sort: background items first, then by sortOrder
  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const aBg = a.config?.isBackground ? -1 : 0;
        const bBg = b.config?.isBackground ? -1 : 0;
        return aBg - bBg || a.sortOrder - b.sortOrder;
      }),
    [items],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <button
            onClick={zoomOut}
            className="p-1.5 rounded border bg-white hover:bg-gray-50 text-gray-700"
            title="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <button
            onClick={resetZoom}
            className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-sm text-gray-700 min-w-[52px] text-center"
            title="Reset zoom"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            onClick={zoomIn}
            className="p-1.5 rounded border bg-white hover:bg-gray-50 text-gray-700"
            title="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
          <button
            onClick={resetZoom}
            className="p-1.5 rounded border bg-white hover:bg-gray-50 text-gray-700 ml-1"
            title="Reset zoom"
          >
            <RotateCcw size={16} />
          </button>
        </div>
        {canPrint && (
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-gray-700"
          >
            <Printer size={15} />
            Print
          </button>
        )}
      </div>

      {/* Canvas */}
      <div className="overflow-auto rounded-lg border bg-gray-100" style={{ maxHeight: '70vh' }}>
        <div
          style={{
            width: canvasW * zoom,
            height: canvasH * zoom,
            position: 'relative',
            transformOrigin: 'top left',
          }}
        >
          <svg
            ref={svgRef}
            data-testid="client-floor-plan-canvas"
            width={canvasW * zoom}
            height={canvasH * zoom}
            viewBox={`0 0 ${canvasW} ${canvasH}`}
            style={{ display: 'block', background: '#f9fafb' }}
          >
            {sortedItems.map((item) => (
              <CanvasItemShape
                key={item.id}
                item={item}
                scale={zoom}
                isSelected={false}
                seated={0}
                onClick={NOOP}
                onDragEnd={NOOP}
                showChairs={false}
                showLabel
                showTableNumbers={isTableItem(item)}
                showIcons
              />
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}
